import type { ReactNode } from 'react'
import { useFormContext } from 'react-hook-form'
import { useListIntegrations } from '~/domains/account/hooks/use-list-integrations.hook'
import { DialogTitle, DialogHeader } from '@cocoonspace/ui/components/dialog'
import { FormField } from '@cocoonspace/ui/components/form'
import { cn } from '@cocoonspace/ui/lib/utils'
import { useLingui } from '@lingui/react/macro'

import { CheckStateIcon } from '~/components/check-state-icon'
import { PageLoader } from '~/components/page-loader'
import { SoonBadge } from '../soon-badge'
import { NextBtn } from './next-btn'
import { ValidateBtn } from './validate-btn'
import type { LocationSchema } from '../../event-type-form'

const MAX_SPACES = 5

const LocationTypeSelector = ({
	title,
	description,
	disabled = false,
	value,
	onChange,
	radioValue,
}: {
	title: string | ReactNode
	description: string | ReactNode
	disabled?: boolean
	value: string
	radioValue: string
	onChange: (value: string) => void
}) => {
	return (
		<button
			type='button'
			className={cn(
				'hover:border-primary group flex cursor-pointer items-center gap-4 rounded-lg border p-4 text-left',
				{
					'hover:border-border cursor-not-allowed opacity-70': disabled,
					'border-primary': radioValue === value,
				},
			)}
			disabled={disabled}
			onClick={() => onChange(radioValue)}
		>
			<CheckStateIcon isActive={radioValue === value} />

			<div>
				<div className='relative font-bold'>{title}</div>
				<div className='text-sm'>{description}</div>
			</div>
		</button>
	)
}

export const StepLocationType = () => {
	const { t } = useLingui()

	const { isLoading, data: userIntegrations } = useListIntegrations()
	const hasGoogleCalendar = userIntegrations?.google?.connected
	const hasOutlookCalendar = userIntegrations?.outlook?.connected
	const hasIntegrations =
		!isLoading && (hasGoogleCalendar || hasOutlookCalendar)

	const form = useFormContext<LocationSchema>()
	const type = form.watch('type')

	if (isLoading) return <PageLoader />

	return (
		<>
			<DialogHeader className='p-6 pb-0'>
				<DialogTitle>{t`Sélectionnez le type de lieu`}</DialogTitle>
			</DialogHeader>

			<div className='flex w-full flex-1 flex-col gap-4 overflow-hidden p-6'>
				<FormField
					name='type'
					render={({ field }) => (
						<LocationTypeSelector
							title={t`Cocoon`}
							description={t`Sélectionnez une adresse et jusqu'à ${MAX_SPACES} Cocoons et finalisez votre réservation à la réception de la réponse de votre invité.`}
							radioValue='cocoon'
							{...field}
						/>
					)}
				/>

				<FormField
					name='type'
					render={({ field }) => (
						<LocationTypeSelector
							title={
								<>
									{t`Mes lieux`} <SoonBadge className='absolute ml-2' />
								</>
							}
							description={t`Sélectionnez un de vos lieux.`}
							radioValue='custom'
							disabled
							{...field}
						/>
					)}
				/>

				<FormField
					name='type'
					render={({ field }) => (
						<LocationTypeSelector
							title={t`Visio-conférence`}
							description={t`Aucune localisation, tout se passe à distance.`}
							radioValue='remote'
							{...field}
						/>
					)}
				/>

				<div className='flex items-center gap-4'>
					{type === 'remote' && (
						<>{hasIntegrations ? <ValidateBtn /> : <NextBtn />}</>
					)}

					{type === 'cocoon' && <NextBtn />}
				</div>
			</div>
		</>
	)
}
